/**
 * Граф связей: что где стоит и что чему родня.
 *
 * Большой граф не читают целиком — его читают по частям, и всё устройство
 * этого экрана про это. Способы, которыми это обычно решают, и что из них
 * взято:
 *
 *   раскладка считается, а не пляшет — узел всегда на одном месте, иначе
 *     запомнить картинку нельзя и каждый заход начинается заново;
 *   фокус с окружением — показать не всё, а выбранное с роднёй; главный
 *     приём против «слишком много узлов», потому что уменьшает не размер
 *     изображения, а само число нарисованного;
 *   отбор по тревоге — тридцать зелёных узлов не несут работы, две красных
 *     несут всю;
 *   отступы вместо линий — дерево листается и не ломается ни на каком
 *     размере, тогда как схема со связями перестаёт читаться сотни на две;
 *   перенос длинных столбцов — тридцать машин в колонку не влезают ни в
 *     какой экран, и колонка ломается на подколонки;
 *   масштаб и закрепление выбора — чтобы можно было отвести взгляд, не
 *     потеряв подсветку.
 *
 * Чего здесь намеренно НЕТ. Свободного графа с пружинами: при каждом открытии
 * другая картинка, и владелец, запомнивший «магазин слева внизу», ищет его
 * заново. Плитки по объёму: объём известен у меньшинства узлов, и плитка
 * показала бы «пусто» там, где на самом деле «не мерили».
 */
import React, { useMemo, useState } from 'react';
import type { GraphNode, GraphSnapshot } from '../../shared/protocol/topology.ts';
import { bytes } from '../lib/format.ts';

export type GraphMode = 'columns' | 'rows' | 'tree' | 'focus' | 'alarms';

export const GRAPH_MODES: { id: GraphMode; label: string; hint: string }[] = [
  { id: 'columns', label: 'Столбцы', hint: 'Уровни слева направо, потомки под родителем' },
  { id: 'rows', label: 'Строки', hint: 'Те же уровни сверху вниз' },
  { id: 'tree', label: 'Дерево', hint: 'Отступами, без линий — листается на любом размере' },
  { id: 'focus', label: 'Фокус', hint: 'Только выбранный узел, его предки и потомки' },
  { id: 'alarms', label: 'Тревоги', hint: 'Только сломанное и отставшее — без «не решено»' },
];

const NODE_W = 190;
const NODE_H = 34;
const GAP = 10;
const LANE_GAP = 70;
const HEAD = 34;
/** Больше этого столбец ломается на подколонки: тридцать машин не влезают. */
const MAX_IN_LANE = 14;

const KIND_GLYPH: Record<GraphNode['kind'], string> = {
  location: '◍', cloud: '☁', hypervisor: '▤', vm: '▢',
  docker: '⬢', app: '◈', data: '▥', asset: '⌁',
};

interface Placed extends GraphNode { x: number; y: number }

/** Предки узла, от ближайшего вверх. */
function ancestors(key: string, byKey: Map<string, GraphNode>): string[] {
  const out: string[] = [];
  const seen = new Set<string>([key]);
  let cursor = byKey.get(key)?.parent ?? null;
  while (cursor && !seen.has(cursor)) {
    out.push(cursor);
    seen.add(cursor);
    cursor = byKey.get(cursor)?.parent ?? null;
  }
  return out;
}

/** Узел и всё, что под ним. */
function descendants(key: string, nodes: GraphNode[]): Set<string> {
  const out = new Set<string>([key]);
  // Узлы идут обходом дерева, поэтому одного прохода достаточно: родитель
  // всегда встречается раньше своих потомков.
  for (const n of nodes) {
    if (n.parent && out.has(n.parent)) out.add(n.key);
  }
  return out;
}

export function HierarchyGraph({ snapshot, mode, onPick }: {
  snapshot: GraphSnapshot;
  mode: GraphMode;
  onPick?: (node: GraphNode | null) => void;
}): React.JSX.Element {
  const [hover, setHover] = useState<string | null>(null);
  const [pinned, setPinned] = useState<string | null>(null);
  const [service, setService] = useState<string | null>(null);
  const [scale, setScale] = useState(1);

  const byKey = useMemo(
    () => new Map(snapshot.nodes.map((n) => [n.key, n])),
    [snapshot],
  );

  /**
   * Какие узлы вообще рисуем.
   *
   * Фокус и тревоги уменьшают не картинку, а число нарисованного — это и есть
   * главный приём против большого графа. Предки в обоих случаях остаются:
   * узел без пути к площадке не говорит, где он стоит, а это первое, что
   * спрашивают.
   */
  const shown = useMemo(() => {
    if (mode === 'focus') {
      const anchor = pinned ?? hover;
      if (!anchor) return snapshot.nodes;
      const keep = descendants(anchor, snapshot.nodes);
      for (const a of ancestors(anchor, byKey)) keep.add(a);
      return snapshot.nodes.filter((n) => keep.has(n.key));
    }
    if (mode === 'alarms') {
      // «Не знаем» сюда НЕ идёт, и это не придирка. «Копия сломана» и «не
      // решено, нужна ли копия» — разные вопросы к разным людям: первый к
      // тому, кто чинит, второй к владельцу. Смешать их значит утопить
      // тридцать четыре поломки в двадцати четырёх нерешённых.
      const ALARM = new Set(['warn', 'stale', 'failed', 'missing']);
      const keep = new Set<string>();
      for (const n of snapshot.nodes) {
        if (!ALARM.has(n.verdict) || n.lifecycle) continue;
        keep.add(n.key);
        for (const a of ancestors(n.key, byKey)) keep.add(a);
      }
      return snapshot.nodes.filter((n) => keep.has(n.key));
    }
    return snapshot.nodes;
  }, [mode, snapshot.nodes, pinned, hover, byKey]);

  /**
   * Раскладка. Считается, а не пляшет: узел всегда там, где был в прошлый раз.
   *
   * Длинные полосы ломаются на подполосы — тридцать машин в колонку не влезают
   * ни в какой экран, а уменьшать масштаб до нечитаемости бессмысленно.
   */
  const { placed, width, height, laneLabels } = useMemo(() => {
    const vertical = mode === 'rows';
    const counts = new Map<number, number>();
    for (const n of shown) counts.set(n.column, (counts.get(n.column) ?? 0) + 1);

    // Где начинается каждый уровень: длинный ломается на несколько полос, и
    // следующий уровень должен встать за всеми ними, а не за первой.
    const offset = new Map<number, number>();
    let cursor = 0;
    for (const col of [...counts.keys()].sort((a, b) => a - b)) {
      offset.set(col, cursor);
      cursor += Math.max(1, Math.ceil((counts.get(col) ?? 0) / MAX_IN_LANE));
    }

    const rowIn = new Map<number, number>();
    const out: Placed[] = [];
    for (const n of shown) {
      const row = rowIn.get(n.column) ?? 0;
      rowIn.set(n.column, row + 1);
      const sub = Math.floor(row / MAX_IN_LANE);
      const inSub = row % MAX_IN_LANE;
      const lane = (offset.get(n.column) ?? 0) + sub;

      out.push({
        ...n,
        x: vertical ? inSub * (NODE_W + GAP) : lane * (NODE_W + LANE_GAP),
        y: vertical ? HEAD + lane * (NODE_H + LANE_GAP) : HEAD + inSub * (NODE_H + GAP),
      });
    }

    const totalLanes = cursor;
    const longest = Math.min(MAX_IN_LANE, Math.max(1, ...counts.values()));
    return {
      placed: out,
      laneLabels: snapshot.columns
        .filter((c) => (counts.get(c.index) ?? 0) > 0)
        .map((c) => ({
          ...c,
          count: counts.get(c.index) ?? 0,
          at: offset.get(c.index) ?? 0,
        })),
      width: vertical
        ? longest * (NODE_W + GAP)
        : totalLanes * (NODE_W + LANE_GAP) - LANE_GAP,
      height: vertical
        ? HEAD + totalLanes * (NODE_H + LANE_GAP)
        : HEAD + longest * (NODE_H + GAP),
    };
  }, [shown, mode, snapshot.columns]);

  /** Что подсвечено: закреплённое сильнее наведённого. */
  const lit = useMemo(() => {
    if (service) {
      const group = snapshot.kinship.find((k) => k.service === service);
      return new Set(group?.members ?? []);
    }
    const anchor = pinned ?? hover;
    if (!anchor) return null;
    const set = descendants(anchor, snapshot.nodes);
    for (const a of ancestors(anchor, byKey)) set.add(a);
    return set;
  }, [pinned, hover, service, snapshot.nodes, snapshot.kinship, byKey]);

  const dim = (key: string): boolean => lit !== null && !lit.has(key);

  const pick = (node: GraphNode): void => {
    // Закрепление, а не только наведение: отвести взгляд, полистать и вернуться
    // — обычное дело на большом графе, а подсветка по наведению этого не даёт.
    const next = pinned === node.key ? null : node.key;
    setPinned(next);
    onPick?.(next ? node : null);
  };

  const vertical = mode === 'rows';

  return (
    <div className="graph">
      <div className="graph__bar">
        {snapshot.kinship.length > 0 && (
          <span className="graph__kin">
            <span className="muted">копии одного приложения:</span>
            {snapshot.kinship.map((k) => (
              <button
                key={k.service}
                type="button"
                className={`chip${service === k.service ? ' chip--on' : ''}`}
                onClick={() => setService(service === k.service ? null : k.service)}
                title={`${k.members.length} копий — подсветить`}
              >
                {k.label} <span className="muted">{k.members.length}</span>
              </button>
            ))}
          </span>
        )}

        <span className="graph__zoom">
          {pinned && (
            <button type="button" className="chip chip--on"
                    onClick={() => { setPinned(null); onPick?.(null); }}>
              снять выбор
            </button>
          )}
          <button type="button" className="chip" onClick={() => setScale((v) => Math.max(0.4, v - 0.15))}
                  title="Мельче">−</button>
          <span className="graph__scale">{Math.round(scale * 100)}%</span>
          <button type="button" className="chip" onClick={() => setScale((v) => Math.min(2, v + 0.15))}
                  title="Крупнее">+</button>
          <button type="button" className="chip" onClick={() => setScale(1)} title="Обычный размер">1:1</button>
        </span>
      </div>

      {mode === 'tree' ? (
        <TreeView
          nodes={shown}
          pinned={pinned}
          dim={dim}
          onPick={pick}
          onHover={setHover}
        />
      ) : (
        <div className="graph__scroll">
          {mode === 'focus' && !pinned && !hover && (
            <p className="muted graph__hint">
              Наведите или выберите узел — покажем его, предков и потомков.
            </p>
          )}
          <svg
            className="graph__svg"
            width={width * scale}
            height={height * scale}
            viewBox={`0 0 ${width} ${height}`}
            role="img"
            aria-label="Граф связей"
          >
            {laneLabels.map((c) => (
              <text
                key={c.index}
                className="graph__col"
                x={vertical ? 0 : c.at * (NODE_W + LANE_GAP)}
                y={vertical ? HEAD + c.at * (NODE_H + LANE_GAP) - 10 : 16}
              >
                {c.label} <tspan className="graph__col-count">{c.count}</tspan>
              </text>
            ))}

            <g className="graph__links">
              {placed.map((node) => {
                const parent = node.parent ? placed.find((p) => p.key === node.parent) : null;
                if (!parent) return null;
                const [x1, y1] = vertical
                  ? [parent.x + NODE_W / 2, parent.y + NODE_H]
                  : [parent.x + NODE_W, parent.y + NODE_H / 2];
                const [x2, y2] = vertical
                  ? [node.x + NODE_W / 2, node.y]
                  : [node.x, node.y + NODE_H / 2];
                const d = vertical
                  ? `M ${x1} ${y1} C ${x1} ${(y1 + y2) / 2}, ${x2} ${(y1 + y2) / 2}, ${x2} ${y2}`
                  : `M ${x1} ${y1} C ${(x1 + x2) / 2} ${y1}, ${(x1 + x2) / 2} ${y2}, ${x2} ${y2}`;
                const faded = dim(node.key) && dim(parent.key);
                return (
                  <path key={`${parent.key}->${node.key}`}
                        className={`graph__link${faded ? ' graph__link--dim' : ''}`} d={d} />
                );
              })}
            </g>

            {placed.map((node) => (
              <g
                key={node.key}
                className={`graph__node graph__node--${node.verdict}`
                  + (node.lifecycle ? ' graph__node--dim' : '')
                  + (dim(node.key) ? ' graph__node--faded' : '')
                  + (pinned === node.key ? ' graph__node--pinned' : '')}
                transform={`translate(${node.x} ${node.y})`}
                tabIndex={0}
                role="button"
                aria-pressed={pinned === node.key}
                aria-label={node.label}
                onMouseEnter={() => setHover(node.key)}
                onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(node.key)}
                onBlur={() => setHover(null)}
                onClick={() => pick(node)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') pick(node); }}
              >
                <rect className="graph__box" width={NODE_W} height={NODE_H} rx={7} />
                <text className="graph__glyph" x={11} y={NODE_H / 2 + 4}>{KIND_GLYPH[node.kind]}</text>
                <text className="graph__label" x={28} y={NODE_H / 2 + 4}>
                  {node.label.length > 22 ? `${node.label.slice(0, 21)}…` : node.label}
                </text>
                {node.bytes != null && (
                  <text className="graph__bytes" x={NODE_W - 10} y={NODE_H / 2 + 4}>{bytes(node.bytes)}</text>
                )}
                <title>{[
                  node.label,
                  node.factName ? `на машине: ${node.factName}` : null,
                  node.note ?? null,
                  node.agentSite ? `снимает агент: ${node.agentSite}` : null,
                ].filter(Boolean).join('\n')}</title>
              </g>
            ))}
          </svg>
        </div>
      )}

      {shown.length === 0 && (
        <p className="muted graph__hint">
          {mode === 'alarms' ? 'Тревог нет — всё в норме.' : 'Показывать нечего.'}
        </p>
      )}
    </div>
  );
}

/**
 * Дерево отступами.
 *
 * Единственный вид, который не ломается ни на каком размере: линий нет,
 * пересекаться нечему, а глубина читается по отступу. Для длинных списков —
 * машин на гипервизоре, копий в приложении — он лучше схемы, и на больших
 * хозяйствах именно им и пользуются.
 */
function TreeView({ nodes, pinned, dim, onPick, onHover }: {
  nodes: GraphNode[];
  pinned: string | null;
  dim: (key: string) => boolean;
  onPick: (node: GraphNode) => void;
  onHover: (key: string | null) => void;
}): React.JSX.Element {
  return (
    <ul className="tree">
      {nodes.map((n) => (
        <li
          key={n.key}
          className={`tree__row tree__row--${n.verdict}`
            + (dim(n.key) ? ' tree__row--faded' : '')
            + (pinned === n.key ? ' tree__row--pinned' : '')}
          style={{ paddingLeft: `${n.column * 1.6 + 0.4}rem` }}
          onMouseEnter={() => onHover(n.key)}
          onMouseLeave={() => onHover(null)}
        >
          <button type="button" className="tree__btn" onClick={() => onPick(n)}>
            <span className="tree__glyph" aria-hidden="true">{KIND_GLYPH[n.kind]}</span>
            <span className="tree__label">{n.label}</span>
            {n.factName && <span className="tree__fact mono">{n.factName}</span>}
            {n.role && <span className="tree__role">{n.role}</span>}
            {n.bytes != null && <span className="tree__bytes">{bytes(n.bytes)}</span>}
          </button>
        </li>
      ))}
    </ul>
  );
}
